import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../../Shared/API/config';

export interface StudentClass {
  displayName: string;
  id: number;
  level?: string;
  levelId?: number;
}

export interface StudentInfo {
  id: number;
  name: string;
  number: string;
  class: StudentClass;
}

export interface FeesInfo {
  /** The term the amounts come from; null when the school has no term. */
  session?: SessionInfo | null;
  total: number;
  paid: number;
  balance: number;
  discount: number;
  writeoff: number;
  credit: number;
}

export interface AttendanceInfo {
  total: number;
  present: number;
  absent: number;
  lastAbsent: string | null;
}

export interface Payment {
  id: number;
  amount: number;
  date: string;
  status: 'collected' | 'pending' | 'failed';
  receiptNumber: string;
  isDiscount: boolean;
  isWriteOff: boolean;
}

export interface BillCharge {
  id: number;
  description: string;
  amount: number;
}

export interface BillsInfo {
  id: number;
  total: number | null;
  displayName: string;
  charges: BillCharge[];
  session?: SessionInfo;
  latest?: boolean;
  published?: boolean;
}

export interface SubscriptionInfo {
  id: number;
  itemName: string;
  frequency: string;
  balance: number;
  totalBilled: number;
  totalPaid: number;
}

export interface SessionInfo {
  id: number;
  displayName: string;
}

export interface ReportInfo {
  id: number;
  displayName: string;
  session: SessionInfo;
  reportType?: string;
  type: string;
  latest?: boolean;
  published?: boolean;
}

export interface MetaInfo {
  serviceFeeRate: number;
  enableOnlinePayments: boolean;
  momoCheckout?: boolean;
}

export interface SubjectResult {
  displayName: string;
  total: number;
  grade: string;
  remark: string;
}

export interface DetailedReport {
  subjects: SubjectResult[];
  aggregate: number;
  attendance?: {
    present: number;
    totalDays: number;
  };
}

export interface AcademicInsights {
  currentAverage: number;
  prevAverage?: number;
  improvement?: number;
  topSubject?: string;
  lowestSubject?: string;
  status: 'published' | 'pending' | 'improving' | 'declining';
  hasUnviewedReport?: boolean;
}

export interface EnrollmentInfo {
  className?: string;
  sessionName?: string;
  admissionDate?: string;
  house?: string;
  rollNumber?: string;
}

export interface AttendanceSummary {
  attendancePercentage?: number;
}

export interface Notice {
  title: string;
  content: string;
  date: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
}

export interface FeeClearanceInfo {
  configured: boolean;
  cleared: boolean;
  message: string;
}

export interface ActivationInfo {
  pending: boolean;
  balance: number;
}

export interface SaleItemInfo {
  name: string;
  balance: number;
}

export interface StudentOverview {
  activation?: ActivationInfo;
  fees: FeesInfo;
  feeClearance?: FeeClearanceInfo;
  student: StudentInfo;
  attendance: AttendanceInfo;
  payments: Payment[];
  bills: BillsInfo[];
  subscriptions?: SubscriptionInfo[];
  saleItems?: SaleItemInfo[];
  terminalReports: ReportInfo[];
  meta: MetaInfo;
  enrollment?: EnrollmentInfo;
  attendanceSummary?: AttendanceSummary;
  notices?: Notice[];
}

export interface ChildInfo {
  id: number;
  name: string;
  number: string;
  recipientToken: string;
  class: StudentClass | null;
  relationship: string;
}

export interface ChildrenResponse {
  children: ChildInfo[];
}

export interface FeedAction {
  type: 'open_sheet' | 'open_modal' | 'navigate_tab';
  target: string;
  payload?: Record<string, unknown>;
}

export interface FeedBadge {
  text: string;
  variant: 'emerald' | 'amber' | 'rose' | 'indigo' | 'slate';
}

export interface FeedItem {
  id: string;
  type:
    | 'attendance_today'
    | 'attendance_absence'
    | 'attendance_status'
    | 'attendance_exception'
    | 'notice'
    | 'payment_receipt'
    | 'report_card'
    | 'bill_issued'
    | 'payment_plan'
    | string;
  timestamp: string;
  title: string;
  summary: string;
  badge?: FeedBadge;
  icon?: string;
  action?: FeedAction;
}

export interface AttentionBreakdown {
  label: string;
  amount: number;
}

export interface AttentionData {
  hasDebt: boolean;
  totalOutstanding: number;
  breakdown: AttentionBreakdown[];
  action?: FeedAction;
}

/** `open_sheet` payload of an attendance feed item. */
export interface AttendancePayload {
  todayStatus?: 'present' | 'absent' | 'late' | string | null;
  present?: number;
  absent?: number;
  expected?: number;
  lastAbsent?: string | null;
}

export interface FeedMeta {
  enableOnlinePayments: boolean;
}

export interface ParentFeedResponse {
  student: StudentInfo;
  /** Absent on older API responses; treat as online payments off. */
  meta?: FeedMeta;
  /** Null when Billing is off; absent on older API responses. Treat both as no activation. */
  activation?: ActivationInfo | null;
  /** Null when Billing is off or there is no term. */
  attention: AttentionData | null;
  feed: FeedItem[];
}

export const ParentDashboardApi = createApi({
  reducerPath: 'ParentDashboardApi',
  baseQuery,
  tagTypes: ['Dashboard', 'Children'],
  keepUnusedDataFor: 15,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  endpoints: builder => ({
    getChildren: builder.query<ChildrenResponse, void>({
      query: () => `/parent/children`,
      providesTags: ['Children'],
    }),
    getDashboardData: builder.query<StudentOverview, string | null>({
      query: studentNumber => `/parent/${studentNumber}/dashboard`,
      providesTags: ['Dashboard'],
    }),
    getParentFeed: builder.query<ParentFeedResponse, string | null>({
      query: studentNumber => `/parent/${studentNumber}/feed`,
      providesTags: ['Dashboard'],
    }),
    createParentTermActivationPromise: builder.mutation<
      void,
      {
        studentNumber: string;
        installments: { amount: string; dueDate: string }[];
      }
    >({
      query: ({ studentNumber, installments }) => ({
        url: `/parent/${studentNumber}/term-activation/promise`,
        method: 'POST',
        body: { installments },
      }),
      invalidatesTags: ['Dashboard'],
    }),
  }),
});

export const {
  useGetChildrenQuery,
  useGetDashboardDataQuery,
  useGetParentFeedQuery,
  useCreateParentTermActivationPromiseMutation,
} = ParentDashboardApi;
